export function isOfferCardCarouselFilterSelectionValid(
  selectedFilter: string | undefined,
  filterOptions: Array<{ value: string | number }>,
): boolean {
  return !!selectedFilter && filterOptions.some(option => String(option.value) === selectedFilter)
}

export function shouldResetOfferCardCarouselSelection(
  selectedFilter: string | undefined,
  filterOptions: Array<{ value: string | number }>,
): boolean {
  if (!selectedFilter) {
    return false
  }

  return !isOfferCardCarouselFilterSelectionValid(selectedFilter, filterOptions)
}

export type ResolveOfferCardCarouselDestinationFiltersOptions = {
  /**
   * Feature-owned predicate for synthetic chips (e.g. homepage All) that must keep
   * multi-place / stream base filters, including empty destinationIds.
   */
  shouldKeepBaseFilters?: (selectedFilter: string) => boolean
}

/**
 * Resolves destination / tour-location chip selection onto offer-list filters.
 *
 * When base filters carry multi-place `destinationIds` and the selected chip is not one of
 * those place ids, keep the base filters so the chip does not overwrite them with an invalid
 * destinationId. Callers that introduce synthetic chips must pass `shouldKeepBaseFilters`
 * so empty multi-place / stream bases are preserved without hardcoding feature sentinels here.
 * Selecting a concrete place id clears multi-place / stream flags for single-place light search.
 */
export function resolveOfferCardCarouselDestinationFilters(
  filters: App.OfferListFilters,
  selectedFilter: string | undefined,
  options?: ResolveOfferCardCarouselDestinationFiltersOptions,
): App.OfferListFilters {
  // Feature-declared synthetic chip — keep multi-place / stream base filters, including empty ids.
  if (selectedFilter && options?.shouldKeepBaseFilters?.(selectedFilter)) {
    return filters
  }

  // Non-empty multi-place set: keep base filters when the chip is not one of those place ids.
  if (
    selectedFilter
    && filters.destinationIds?.length
    && !filters.destinationIds.includes(selectedFilter)
  ) {
    return filters
  }

  if (!selectedFilter) {
    return {
      ...filters,
      destinationId: filters.destinationId,
    }
  }

  if (!filters.destinationIds?.length && !filters.useUnifiedSearchEndpoint && !filters.placeIds?.length && !filters.isStream) {
    return {
      ...filters,
      destinationId: selectedFilter,
    }
  }

  const {
    destinationIds: _destinationIds,
    placeIds: _placeIds,
    useUnifiedSearchEndpoint: _useUnifiedSearchEndpoint,
    isStream: _isStream,
    sortBy: _sortBy,
    ...restFilters
  } = filters

  return {
    ...restFilters,
    destinationId: selectedFilter,
  }
}
